import { apiClient } from './client';

/**
 * 업무 보드 확장 API 클라이언트 — ⭐ 정본(`feature/dev-syk`)의 `api/task-extras.ts` 에서
 * ★ **서버가 있고 ⭐ 부르는 곳이 있는 것만** 옮겼다 (2차 2-2′ · 2026-10-08).
 *
 * ## ⛔ 왜 ★ «통째로» 옮기지 않았나
 *
 * 정본의 이 파일은 ★ **다섯 기능**이 섞인 묶음이다 — 체크리스트 «항목» · 서식 ·
 * 업무 그룹 · 커스텀 필드. ⇒ ⛔ 통째로 옮기면 ★ main 에 **없는** 끝점의 클라이언트가
 * ⇒ ⭐ **죽은 코드**로 들어온다(부르는 곳 0 · 서버 0).
 *
 * ```
 * 잰 것 (2026-10-08 · origin/main 의 서버 소스)
 *   ✅ /api/v1/checklist-templates           GET · POST · PATCH /:id · DELETE /:id   ⇐ ★ 담았다
 *   ✅ /api/v1/tasks/:id/checklist           GET · POST · PATCH · DELETE · import
 *      ⇒ ⚠ 서버는 ★ 있다. 그런데 ⭐ 이 판에 ★ «부르는 화면»이 없다 ⇒ ⛔ 담지 않았다
 *      ⇒ ⇒ 업무 상세(체크리스트 UI)를 올리는 판이 ★ 그때 더하면 된다
 *   ⛔ /task-groups · /projects/:id/custom-fields  ⇒ ★ 서버가 ⭐ «없다»
 *      ⇒ 근거: 백엔드 «자신의» 시험이 적어 두었다 —
 *        apps/chat-server/src/lib/task-board-219-migration.test.ts:36
 *        「⛔ 아직이다 (3-3)   task-groups.routes.ts · project-fields.routes.ts」
 * ```
 * ⇒ ⚠ **그러므로 이 파일은 ★ 「정본의 그 파일」이 아니다** — 같은 이름이지만 ⭐ 일부다.
 *   3차에서 그 기능들이 오면 ★ 이 파일에 **더하면** 된다(이름을 정본과 같게 둔 까닭이다).
 *
 * ## ⭐ 서버와 ★ 꼴이 맞는가 — ⭐ 쟀다
 *
 * ```
 * 서버 serializeTemplate (checklist-templates.routes.ts:71-83) 가 돌려주는 칸 ★ 여덟:
 *   id · name · scope · icon · sortOrder · ownerId · isMine · items
 * ⇒ ⇒ ✅ 아래 `ChecklistTemplateDto` 와 ★ «하나도 안 빠지고» 같다
 * ⇒ 주소도 맞는다: apiClient 의 baseURL 이 '/api/v1' 이므로(api/client.ts:22)
 *   아래 '/checklist-templates' 가 ⇒ ★ '/api/v1/checklist-templates' 로 간다
 * ```
 */

// ─── AW-004-04 체크리스트 템플릿 ─────────────────────────────

export type ChecklistTemplateScope = 'SHARED' | 'PRIVATE';

export interface ChecklistTemplateDto {
  id: string;
  name: string;
  scope: ChecklistTemplateScope;
  icon: string | null;
  sortOrder: number;
  ownerId: string;
  /** 내가 만든 템플릿인가 — 개인 템플릿의 수정·삭제 가능 여부와 같다. */
  isMine: boolean;
  items: string[];
}

export async function listChecklistTemplates(): Promise<ChecklistTemplateDto[]> {
  const res = await apiClient.get<{ templates: ChecklistTemplateDto[] }>('/checklist-templates');
  return Array.isArray(res.data?.templates) ? res.data.templates : [];
}

export async function createChecklistTemplate(input: {
  name: string; scope?: ChecklistTemplateScope; icon?: string | null; items?: string[];
}): Promise<ChecklistTemplateDto> {
  const res = await apiClient.post<ChecklistTemplateDto>('/checklist-templates', input);
  return res.data;
}

export async function patchChecklistTemplate(id: string, input: {
  name?: string; scope?: ChecklistTemplateScope; icon?: string | null; items?: string[]; sortOrder?: number;
}): Promise<ChecklistTemplateDto> {
  const res = await apiClient.patch<ChecklistTemplateDto>(`/checklist-templates/${id}`, input);
  return res.data;
}

export async function deleteChecklistTemplate(id: string): Promise<void> {
  await apiClient.delete(`/checklist-templates/${id}`);
}

// ─── AW-004-01 / AW-004-02 내 업무 묶음 (2차 4-2 · 2026-10-08) ───────────────

/**
 * ⭐ 업무 묶음 — ★ 이 판(4-2)이 ⭐ «부르는 것»만 담았다. ⇒ ⛔ `setTaskGroup` 은 ★ 없다.
 *
 * ```
 * ✅ 담은 것 ⇒ 이 판의 두 모달이 ★ «실제로» 부른다:
 *   listTaskGroups    ⇐ TaskGroupManageModal
 *   createTaskGroup   ⇐ TaskGroupCreateModal
 *   patchTaskGroup    ⇐ TaskGroupManageModal
 *   deleteTaskGroup   ⇐ TaskGroupManageModal
 *   TaskGroupDto      ⇐ 둘 다
 * ⛔ 안 담은 것 ⇒ `setTaskGroup`(업무를 묶음에 넣고 뺀다)
 *   ⇒ 정본에서 ★ 부르는 곳이 ⭐ MyTaskCreateModal(4-5) · MyTaskDetailPanel(4-4) 다
 *   ⇒ ⇒ ★ 지금 담으면 ⭐ «부르는 곳이 0» 인 코드가 된다 ⇒ ⛔ 그 판이 더하면 된다
 * ⚠ 서버는 ★ 다섯 끝점을 ⭐ «다» 가지고 있다(백엔드 3-3 · task-groups.routes.ts):
 *   /task-groups GET·POST · /task-groups/:id PATCH·DELETE · /tasks/:id/group PUT
 *   ⇒ ⇒ ⭐ 즉 ★ 「서버가 없어서」가 ⛔ 아니다 — ★ 「부를 곳이 없어서」다(그 가름을 적어 둔다).
 * ```
 *
 * ## ⭐ 서버와 ★ 꼴이 맞는가 — ⭐ 쟀다
 * ```
 * 서버 serializeGroup (task-groups.routes.ts:62-72) 가 돌려주는 칸 ★ 다섯:
 *   id · name · color · sortOrder · taskIds      ⇒ ✅ 아래 `TaskGroupDto` 와 ★ 같다
 * ⚠ 그 함수도 ★ `serializeGroup(row: {` 꼴이다 — ⇒ ⭐ 가드는 ★ `return {` 부터 자른다
 *   (⇒ 4-1 에서 ★ 그 함정에 ⭐ 걸렸다: 첫 `{` 가 «인자 타입»의 것이어서 ⇒ 칸이 0 ⇒ 공짜 통과)
 * ```
 */
export interface TaskGroupDto {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
  /** 이 묶음에 담긴 업무 id — 목록 화면이 묶음 머리로 모을 때 쓴다. */
  taskIds: string[];
}

export async function listTaskGroups(): Promise<TaskGroupDto[]> {
  const res = await apiClient.get<{ groups: TaskGroupDto[] }>('/task-groups');
  return Array.isArray(res.data?.groups) ? res.data.groups : [];
}

export async function createTaskGroup(input: { name: string; color: string }): Promise<TaskGroupDto> {
  const res = await apiClient.post<TaskGroupDto>('/task-groups', input);
  return res.data;
}

export async function patchTaskGroup(
  id: string,
  input: { name?: string; color?: string; sortOrder?: number },
): Promise<TaskGroupDto> {
  const res = await apiClient.patch<TaskGroupDto>(`/task-groups/${id}`, input);
  return res.data;
}

export async function deleteTaskGroup(id: string): Promise<void> {
  await apiClient.delete(`/task-groups/${id}`);
}

// ─── AW-002-02 사용자 정의 칸 — ⭐ «타입만» (2차 4-1 · 2026-10-08) ───────────

/**
 * ⚠⚠ 여기 ★ «함수»가 없는 까닭 — ⭐ 일부러다.
 *
 * ```
 * 서버는 ★ 있다 (백엔드 3-3 · project-fields.routes.ts · 끝점 ⭐ 다섯):
 *   /projects/:projectId/custom-fields   GET · POST
 *   /custom-fields/:id                   PATCH · DELETE
 *   /tasks/:id/custom-fields/:fieldId     PUT
 * ⇒ ⛔ 그런데 ★ 이 판(4-1)이 담는 세 파일은 ⇒ ⭐ «타입만» 쓴다(그리는 쪽이다).
 *   ⇒ ★ 함수를 부르는 것은 ⭐ `MyTaskDetailPanel` 이고 ⇒ 그것은 ★ 4-4 다.
 * ⇒ ⇒ ⭐ 그래서 ★ 함수를 지금 담으면 ⇒ ⛔ «부르는 곳이 0» 인 코드가 된다.
 *   ⇒ ⚠ 이 레포가 그 꼴을 여러 번 겪었다([[only-caller-is-its-own-test]]).
 * ```
 * ⇒ ⭐ 4-4 가 ★ 이 자리에 함수를 ⭐ «더하면» 된다 — 이 묶음 머리를 그때 고친다.
 *
 * ## ⭐ 서버와 ★ 꼴이 맞는가 — ⭐ 쟀다(2026-10-08)
 *
 * ```
 * 서버 serializeField (project-fields.routes.ts:121-131) 가 돌려주는 칸 ★ 여섯:
 *   id · projectId · name · fieldType · options · sortOrder   ⇒ ✅ 아래 `CustomFieldDto` 와 ★ 같다
 * 서버 FIELD_TYPES ＝ prisma `enum ProjectFieldType` ＝ ★ 아홉:
 *   SINGLE_SELECT · MULTI_SELECT · DATE · STATUS · TEXT · NUMBER · URL · CHECKBOX · MEMBER
 *   ⇒ ✅ 아래 `CustomFieldType` 과 ★ 글자까지 같다 (⭐ 세 쪽이 맞는다)
 * 서버 optionsSchema (:61-62)  label(필수 · 1~40자) · color(선택 · ^#[0-9a-fA-F]{6}$)
 *   ⇒ ✅ 아래 `CustomFieldOption` 과 ★ 같다
 * ⇒ ⭐ 그 맞음을 ★ 가드가 잠근다(`pages/ProjectV2/custom-fields.guard.test.ts`).
 * ```
 */
export type CustomFieldType =
  | 'SINGLE_SELECT' | 'MULTI_SELECT' | 'DATE' | 'STATUS'
  | 'TEXT' | 'NUMBER' | 'URL' | 'CHECKBOX' | 'MEMBER';

export interface CustomFieldOption {
  label: string;
  /** `#RRGGBB` — 서버가 그 꼴만 받는다(없을 수 있다). */
  color?: string;
}

export interface CustomFieldDto {
  id: string;
  projectId: string;
  name: string;
  fieldType: CustomFieldType;
  options: CustomFieldOption[];
  sortOrder: number;
}

/** 한 칸에 담기는 값 — 칸 꼴에 따라 글자·수·참거짓·여럿·빈 것이 된다. */
export type CustomFieldValue = string | number | boolean | string[] | null;

// ─── 2차 4-4 업무 상세 패널 (2026-10-09) — ⭐ 정본 선언을 «글자 그대로» ───────────

/**
 * ⭐⭐ 이 묶음이 ★ 「언제 담나」의 ⭐ 답이다 — ⇒ 위 두 머리말이 ★ 미리 적어 둔 그 날이다.
 *
 * ```
 * 4-1 머리말 ⇒ 「★ 함수를 부르는 것은 ⭐ `MyTaskDetailPanel` 이고 ⇒ 그것은 ★ 4-4 다」
 * 4-2 머리말 ⇒ 「⛔ 안 담은 것 ⇒ `setTaskGroup` … ★ 부르는 곳이 ⭐ MyTaskDetailPanel(4-4) 다」
 * 2-2′ 머리말 ⇒ 「/tasks/:id/checklist … ⚠ 서버는 ★ 있다. 그런데 ⭐ 이 판에 ★ «부르는 화면»이 없다」
 * ⇒ ⇒ ✅ ⭐ 그 셋이 ★ 이 판으로 ⭐ «다» 왔다. ⇒ ★ 그래서 ⭐ 아래 열이 ★ 들어온다.
 * ```
 * ⇒ ⭐ 서버는 ★ 모두 있다(2026-10-09 · main 8def559d0 의 서버 소스에서 전수로 뽑았다):
 * ```
 *   GET·POST /api/v1/tasks/:id/checklist            ⇐ task-checklist.routes.ts
 *   PATCH·DELETE /api/v1/tasks/:id/checklist/:subId ⇐ 같은 파일
 *   POST /api/v1/tasks/:id/checklist/import         ⇐ 같은 파일
 *   PUT  /api/v1/tasks/:id/group                    ⇐ task-groups.routes.ts
 *   GET  /api/v1/projects/:projectId/custom-fields  ⇐ project-fields.routes.ts
 *   PUT  /api/v1/tasks/:id/custom-fields/:fieldId   ⇐ 같은 파일
 * ```
 * ⚠ 그리고 ★ `listChecklistTemplates` 는 ⭐ 2-2′ 가 «이미» 담았다 — ⇒ ⛔ 두 벌로 안 만든다.
 */
export interface ChecklistItemDto {
  id: string;
  content: string;
  isDone: boolean;
  sortOrder: number;
}

export async function listChecklist(taskId: string): Promise<ChecklistItemDto[]> {
  const res = await apiClient.get<{ items: ChecklistItemDto[] }>(`/tasks/${taskId}/checklist`);
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

export async function addChecklistItem(taskId: string, content: string): Promise<ChecklistItemDto> {
  const res = await apiClient.post<ChecklistItemDto>(`/tasks/${taskId}/checklist`, { content });
  return res.data;
}

export async function patchChecklistItem(
  taskId: string,
  itemId: string,
  input: { content?: string; isDone?: boolean; sortOrder?: number },
): Promise<ChecklistItemDto> {
  const res = await apiClient.patch<ChecklistItemDto>(`/tasks/${taskId}/checklist/${itemId}`, input);
  return res.data;
}

export async function deleteChecklistItem(taskId: string, itemId: string): Promise<void> {
  await apiClient.delete(`/tasks/${taskId}/checklist/${itemId}`);
}

/** 템플릿의 기본 항목을 이 업무로 복사(스냅샷). 상한에 걸리면 일부만 들어갈 수 있어 건수를 함께 준다. */
export async function importChecklistTemplate(
  taskId: string,
  templateId: string,
): Promise<{ items: ChecklistItemDto[]; addedCount: number; skippedCount: number }> {
  const res = await apiClient.post<{ items: ChecklistItemDto[]; addedCount: number; skippedCount: number }>(
    `/tasks/${taskId}/checklist/import`, { templateId },
  );
  return {
    items: Array.isArray(res.data?.items) ? res.data.items : [],
    addedCount: res.data?.addedCount ?? 0,
    skippedCount: res.data?.skippedCount ?? 0,
  };
}

/** 업무를 내 그룹에 넣거나(groupId) 뺀다(null). 한 업무는 내 그룹 중 하나에만 속한다. */
export async function setTaskGroup(taskId: string, groupId: string | null): Promise<void> {
  await apiClient.put(`/tasks/${taskId}/group`, { groupId });
}

export interface CustomFieldValueDto {
  fieldId: string;
  taskId: string;
  value: CustomFieldValue;
}

export async function listCustomFields(projectId: string): Promise<{
  fields: CustomFieldDto[];
  values: CustomFieldValueDto[];
}> {
  const res = await apiClient.get<{ fields: CustomFieldDto[]; values: CustomFieldValueDto[] }>(
    `/projects/${projectId}/custom-fields`,
  );
  return {
    fields: Array.isArray(res.data?.fields) ? res.data.fields : [],
    values: Array.isArray(res.data?.values) ? res.data.values : [],
  };
}

export async function setCustomFieldValue(
  taskId: string,
  fieldId: string,
  value: CustomFieldValue,
): Promise<CustomFieldValueDto> {
  const res = await apiClient.put<CustomFieldValueDto>(`/tasks/${taskId}/custom-fields/${fieldId}`, { value });
  return res.data;
}

// ─── 정본 이관 판 6 (2026-10-13) — 사용자 정의 칸 만들기·고치기·지우기 ───────────
//   ⭐ 정본 선언 «글자 그대로». 부르는 곳 ⇒ pages/TaskPage/TaskTableView.tsx(목록보기 [＋] 칸 메뉴).
//   ⇒ 4-1 머리말의 「부르는 곳이 생기면 더한다」가 이 판에서 섰다.
//   서버 ⇒ project-fields.routes.ts  POST /projects/:projectId/custom-fields · PATCH·DELETE /custom-fields/:id
export async function createCustomField(projectId: string, input: {
  name: string; fieldType: CustomFieldType; options?: CustomFieldOption[];
}): Promise<CustomFieldDto> {
  const res = await apiClient.post<CustomFieldDto>(`/projects/${projectId}/custom-fields`, input);
  return res.data;
}

export async function patchCustomField(id: string, input: {
  name?: string; options?: CustomFieldOption[]; sortOrder?: number;
}): Promise<CustomFieldDto> {
  const res = await apiClient.patch<CustomFieldDto>(`/custom-fields/${id}`, input);
  return res.data;
}

export async function deleteCustomField(id: string): Promise<void> {
  await apiClient.delete(`/custom-fields/${id}`);
}
